import { useState } from "react";
import { Box, Button, Container, Typography, Paper, Chip, Stack, Divider, Link, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import LanguageIcon from "@mui/icons-material/Language";
import HttpIcon from "@mui/icons-material/Http";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useNavigate } from "react-router-dom";
import { colors } from "@components/ui/colors";
import { RiveByKey } from "@components/riveAnimation";

export const ErrorHandler = ({ error, resetErrorBoundary }) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const currentPath = window.location.pathname;
  const isDev = process.env.NODE_ENV === "development" || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const httpStatus = error?.status || error?.statusCode || error?.response?.status || error?.message?.match(/\b(4\d\d|5\d\d)\b/)?.[0] || null;

  const errorMessage =
    error?.response?.data?.message || error?.response?.data?.error || error?.message || "An unexpected application error occurred.";

  const apiUrl = error?.config?.url || error?.response?.config?.url || null;
  const apiMethod = error?.config?.method?.toUpperCase() || null;

  const handleReload = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }

    window.location.reload();
  };

  const handleGoHome = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }
    navigate("/flow-hub", { replace: true });
  };

  const handleCopyError = () => {
    const rawData = {
      status: httpStatus,
      message: errorMessage,
      path: currentPath,
      apiUrl,
      apiMethod,
      name: error?.name,
      stack: error?.stack,
    };
    navigator.clipboard.writeText(JSON.stringify(rawData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        p: { xs: 2, sm: 3 },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3.5, sm: 6 },
            borderRadius: "24px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Box textAlign="center">
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{}}>
              <RiveByKey riveKey="layout" boxSize={200} />
            </Box>

            <Typography variant="h4" fontWeight={800} color="var(--fx-surface-text, #0F172A)" gutterBottom sx={{ letterSpacing: "-0.02em" }}>
              Something went wrong
            </Typography>

            <Typography variant="body1" color="var(--fx-surface-textsecondary, #64748B)" sx={{ maxWidth: 460, mx: "auto", lineHeight: 1.3, fontSize: "0.95rem" }}>
              We're sorry, an unexpected issue prevented this section from loading correctly. You can refresh the page or return to your workspace.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
              <Button
                variant="contained"
                disableElevation
                startIcon={<RefreshIcon />}
                onClick={handleReload}
                sx={{
                  bgcolor: colors.orange.base,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.2,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "0.9rem",
                  "&:hover": {
                    bgcolor: colors.orange[600],

                    "& .MuiButton-startIcon": {
                      animation: "spin 0.6s ease-in-out",
                    },
                  },

                  "@keyframes spin": {
                    "0%": {
                      transform: "rotate(0deg)",
                    },
                    "100%": {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              >
                Reload page
              </Button>

              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoHome}
                sx={{
                  color: colors.orange.base,
                  borderColor: "var(--fx-surface-border, #E2E8F0)",
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.2,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "0.9rem",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  "&:hover": {
                    borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",

                    "& .MuiButton-startIcon": {
                      animation: "backAndForth 0.6s ease-in-out",
                    },
                  },

                  "@keyframes backAndForth": {
                    "0%": { transform: "translateX(0)" },
                    "50%": { transform: "translateX(-5px)" },
                    "100%": { transform: "translateX(0)" },
                  },
                }}
              >
                Back to Dashboard
              </Button>
            </Stack>

            <Typography variant="caption" color="var(--fx-surface-texttertiary, #94A3B8)" display="block" sx={{ mt: 3.5 }}>
              Need help?{" "}
              <Link href="mailto:support@flownect.com" underline="hover" sx={{ color: "#F97316", fontWeight: 700, cursor: "pointer" }}>
                Contact Support
              </Link>
            </Typography>
          </Box>

          {isDev && (
            <>
              <Divider sx={{ my: 4, borderColor: "var(--fx-surface-border, #E2E8F0)" }} />

              <Box
                sx={{
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: "18px",
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                }}
              >
                {/* Header Dev */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {httpStatus ? (
                      <Chip
                        label={`HTTP ${httpStatus}`}
                        sx={{
                          bgcolor: Number(httpStatus) >= 500 ? "var(--fx-colors-danger-100, #FEE2E2)" : "var(--fx-colors-yellow-100, #FFF9C2)",
                          color: Number(httpStatus) >= 500 ? "#991B1B" : "#92400E",
                          fontWeight: 800,
                          borderRadius: "8px",
                          fontFamily: "monospace",
                        }}
                        size="small"
                      />
                    ) : (
                      <Chip
                        label={error?.name || "RUNTIME_ERROR"}
                        sx={{
                          bgcolor: "var(--fx-colors-danger-100, #FEE2E2)",
                          color: "#991B1B",
                          fontWeight: 800,
                          borderRadius: "8px",
                          fontFamily: "monospace",
                        }}
                        size="small"
                      />
                    )}
                    <Typography variant="subtitle2" fontWeight={700} color="var(--fx-surface-textsecondary, #64748B)">
                      Developer Inspector
                    </Typography>
                  </Stack>

                  <Button
                    size="small"
                    startIcon={copied ? <CheckRoundedIcon /> : <ContentCopyIcon />}
                    onClick={handleCopyError}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: copied ? "#16A34A" : "var(--fx-surface-textsecondary, #64748B)",
                    }}
                  >
                    {copied ? "Copied" : "Copy JSON"}
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "var(--fx-colors-danger-50, #FEF2F2)", border: "1px solid #FCA5A5", borderRadius: "12px" }}>
                    <Typography variant="caption" fontWeight={800} color="#991B1B" display="block" mb={0.5}>
                      EXCEPTION / MESSAGE
                    </Typography>
                    <Typography variant="body2" color="#991B1B" fontWeight={600} fontFamily="monospace">
                      {errorMessage}
                    </Typography>
                  </Paper>

                  {/* Grid de Metadatos */}
                  <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: "var(--fx-surface-base, #FFFFFF)", border: "1px solid var(--fx-surface-border, #E2E8F0)", borderRadius: "12px" }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <LanguageIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />
                        <Typography variant="caption" fontWeight={700} color="var(--fx-surface-textsecondary, #64748B)">
                          FRONTEND ROUTE
                        </Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600} color="var(--fx-surface-text, #0F172A)" fontFamily="monospace">
                        {currentPath}
                      </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: "var(--fx-surface-base, #FFFFFF)", border: "1px solid var(--fx-surface-border, #E2E8F0)", borderRadius: "12px" }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <HttpIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />
                        <Typography variant="caption" fontWeight={700} color="var(--fx-surface-textsecondary, #64748B)">
                          API ENDPOINT
                        </Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600} color="var(--fx-surface-text, #0F172A)" fontFamily="monospace">
                        {apiUrl ? `${apiMethod || ""} ${apiUrl}` : "N/A (React UI Render)"}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Stack Trace Code Box */}
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <CodeIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />
                      <Typography variant="caption" fontWeight={700} color="var(--fx-surface-textsecondary, #64748B)">
                        STACK TRACE
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#1E293B",
                        color: "#38BDF8",
                        borderRadius: "12px",
                        maxHeight: 200,
                        overflowY: "auto",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {error?.stack || JSON.stringify(error, null, 2)}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};
